import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';
import { IConflictCheckParams } from '../interface/conflictCheck';

interface ConflictCheckContextType {
  conflictCheckParams: IConflictCheckParams | null;
  initialConflictCheckParams: IConflictCheckParams | null;
  updateConflictCheckParams: (params: IConflictCheckParams | null) => void;
  clearInitialConflictCheckParams: () => void;
}

const ConflictCheckContext = createContext<ConflictCheckContextType | undefined>(undefined);

export const ConflictCheckProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [conflictCheckParams, setConflictCheckParams] = useState<IConflictCheckParams | null>(null);
  const [initialConflictCheckParams, setInitialConflictCheckParams] =
    useState<IConflictCheckParams | null>(null);

  const updateConflictCheckParams = useCallback((params: IConflictCheckParams | null) => {
    if (params) {
      setInitialConflictCheckParams((currentInitialParams) => currentInitialParams ?? params);
    }
    setConflictCheckParams(params);
  }, []);
  const clearInitialConflictCheckParams = useCallback(() => {
    setInitialConflictCheckParams(null);
  }, []);
  const conflictCheckContextValue = useMemo(
    () => ({
      conflictCheckParams,
      updateConflictCheckParams,
      initialConflictCheckParams,
      clearInitialConflictCheckParams,
    }),
    [
      conflictCheckParams,
      updateConflictCheckParams,
      initialConflictCheckParams,
      clearInitialConflictCheckParams,
    ],
  );
  return (
    <ConflictCheckContext.Provider value={conflictCheckContextValue}>
      {children}
    </ConflictCheckContext.Provider>
  );
};

export const useConflictCheck = () => {
  const context = useContext(ConflictCheckContext);
  if (!context) {
    throw new Error('useConflictCheck must be used within a ConflictCheckProvider');
  }
  return context;
};
